import React, { useEffect, useRef } from 'react';
import themeColors from '@config/scss/partials/_colors.module.scss';

/**
 * Renders the AWS WAF CAPTCHA challenge and keeps the shadow-DOM controls in the theme colours.
 * The integration itself is driven by the `REACT_APP_AWS_WAF_*` environment configuration.
 */
const WafCaptcha = ({ show, onVerified, locale = 'de-DE' }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (show && containerRef.current) {
            // Ensure the CAPTCHA is only rendered if it isn't already initialized
            if (window.AwsWafCaptcha && typeof window.AwsWafCaptcha.renderCaptcha === 'function') {
                window.AwsWafCaptcha.renderCaptcha(containerRef.current, {
                    apiKey: process.env.REACT_APP_AWS_WAF_CAPTCHA_KEY,
                    onSuccess: onVerified,
                    dynamicWidth: true,
                    skipTitle: true,
                    disableLanguageSelector: true,
                    defaultLocale: locale,
                });

                // Style injection for the AWS WAF Shadow DOM elements
                const applyShadowStyles = () => {
                    const captchaEl = containerRef.current?.querySelector('awswaf-captcha');
                    if (captchaEl && captchaEl.shadowRoot) {
                        if (!captchaEl.shadowRoot.querySelector('#custom-waf-styles')) {
                            const styleTag = document.createElement('style');
                            styleTag.id = 'custom-waf-styles';
                            styleTag.textContent = `
                                .btn-primary {
                                    background-color: ${themeColors.primary} !important;
                                    border-color: ${themeColors.primary} !important;
                                    color: ${themeColors.white} !important;
                                    font-weight: 700 !important;
                                    border-radius: 0px !important;
                                    height: 36px !important;
                                    padding: 0 16px !important;
                                }
                                .btn-primary:hover,
                                .btn-primary:active,
                                .btn-primary:focus {
                                    filter: brightness(0.9) !important;
                                    background-color: ${themeColors.primary} !important;
                                    border-color: ${themeColors.primary} !important;
                                }
                                .btn-primary:disabled {
                                    opacity: 0.5 !important;
                                    background-color: ${themeColors.primary} !important;
                                    border-color: ${themeColors.primary} !important;
                                    cursor: not-allowed !important;
                                }
                            `;
                            captchaEl.shadowRoot.appendChild(styleTag);
                        }
                    }
                };

                // Check for shadow root immediately and observe for async mounting
                applyShadowStyles();
                const observer = new MutationObserver(() => {
                    applyShadowStyles();
                });
                observer.observe(containerRef.current, { childList: true, subtree: true });

                return () => observer.disconnect();
            } else {
                console.error('AWS WAF CAPTCHA library is not loaded.');
            }
        }
        return undefined;
    }, [show, onVerified, locale]);

    return (
        <div className={`rounded-3 border bg-white p-3 shadow-sm mb-3 ${show ? '' : 'd-none'}`}>
            <div className="mb-4 border-bottom pb-3">
                <h3>Stimme bestätigen</h3>
                <p className="mt-1 small mb-0 text-dark">
                    Lösen Sie kurz das Bilderrätsel. Ihre Stimme wird anschließend direkt gezählt.
                </p>
            </div>
            <div ref={containerRef} className="d-flex justify-content-center" />
            <p className="mt-3 text-center small mb-0 text-muted">Sicherheitsprüfung bereitgestellt durch AWS WAF</p>
        </div>
    );
};

export default WafCaptcha;
